import { FC, useEffect, useMemo, useState } from 'react';

import CurrencyInput from 'react-currency-input-field';
import { BsFillPencilFill } from 'react-icons/bs';
import { HiCheck } from 'react-icons/hi';
import { IoClose } from 'react-icons/io5';
import { CopyIcon } from 'ui/components/icon';
import { cn, copyToClipboard } from 'utils/helpers';

type VoidFunc = (val: string) => void;
type StringFunc = (val: string) => string;

type RuleType = VoidFunc | StringFunc;

interface PROPS {
  title: string;
  value?: string;
  rule?: RuleType;
  onSubmit: (val: string, onSuccess: () => void) => void;
  isLoading?: boolean;
  inputClassname?: string;
  textClassname?: string;
  disabled?: boolean;
  hideCopy?: boolean;
  hideBottomBorder?: boolean;
  hideTitle?: boolean;
  placeholder?: string;
}

const QuickEditNumberPercentage: FC<PROPS> = ({
  title,
  value: _value,
  rule,
  onSubmit,
  hideTitle = false,
  hideCopy = false,
  hideBottomBorder = false,
  inputClassname,
  textClassname,
  isLoading,
  disabled,
  placeholder,
}) => {
  const [value, setValue] = useState(_value ?? '');
  const [isEditMode, setIsEditMode] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (_value) {
      setValue(_value);
    }
  }, [_value]);

  const handleSubmit = () => {
    if (rule) {
      const err = rule(value);

      if (err) {
        setError(err);
        return;
      }
    }

    onSubmit(value, () => {
      setIsEditMode(false);
      setError(null);
    });
  };

  const noValue = useMemo(() => {
    if (placeholder) {
      return placeholder;
    }

    return `no ${title} yet`;
  }, [title, placeholder]);

  return (
    <div
      className={cn(
        !hideBottomBorder &&
          'border-b-[0.5px] border-b-separator-primary py-[14px]',
      )}
    >
      {!hideTitle && <p className="text-sm secondary text-content">{title}</p>}
      {isEditMode ? (
        <div className="flex mt-1 items-start">
          <div className="flex-1">
            <CurrencyInput
              suffix="%"
              className={cn(
                'outline-none text-sm w-full',
                'border-b-[0.5px]',
                error ? 'border-danger-base' : 'border-secondary-light',
                inputClassname,
              )}
              value={value}
              onValueChange={(val) => setValue(val ?? '')}
            />

            {error && <p className="text-xs text-danger-base">{error}</p>}
          </div>
          <div className="flex items-center gap-x-2">
            <button
              onClick={handleSubmit}
              type="button"
              className={cn(
                'text-primary-base disabled:text-disabled',
                'bg-fill-secondary disabled:bg-transparent',
                'h-6 w-6 rounded-[6px]',
                'flex items-center justify-center',
              )}
            >
              <HiCheck className="h-4 w-4" />
            </button>
            <button
              onClick={() => setIsEditMode(false)}
              type="button"
              className={cn(
                'h-6 w-6 rounded-[6px]',
                'bg-fill-secondary',
                'flex items-center justify-center',
                'shadow-leapify-sm',
              )}
            >
              <IoClose className="h-4 w-4 text-danger-base" />
            </button>
          </div>
        </div>
      ) : (
        <div
          className="flex items-center gap-1"
          onMouseEnter={() => setShowEdit(true)}
          onMouseLeave={() => setShowEdit(false)}
        >
          <p
            className={cn(
              'whitespace-pre-wrap',
              'text-sm primary break-all',
              !_value && 'text-disabled italic lowercase',
              textClassname,
            )}
          >
            {_value ? `${Number(_value).toLocaleString()}%` : noValue}
          </p>
          {!!_value && !hideCopy && (
            <button
              disabled={isLoading}
              className="h-4"
              type="button"
              onClick={() => copyToClipboard(_value)}
            >
              <CopyIcon className="w-4 h-4 text-secondary-base" />
            </button>
          )}
          <button
            disabled={isLoading}
            type="button"
            className={cn(showEdit && !disabled ? 'block' : 'hidden')}
            onClick={() => setIsEditMode(true)}
          >
            <BsFillPencilFill className="w-3 h-3 text-secondary-base" />
          </button>
        </div>
      )}
    </div>
  );
};

export default QuickEditNumberPercentage;
